<template>
	<div class="device-table-section" v-loading="loading">
		<div class="section-header">
			<span class="section-title">异常设备列表</span>
			<div class="section-actions">
				<el-radio-group v-model="alarmSeq" size="mini" @change="handleAlarmSeqChange">
					<el-radio-button label="0">实时</el-radio-button>
					<el-radio-button label="1">历史</el-radio-button>
				</el-radio-group>
				<el-pagination
					small
					layout="prev, pager, next"
					:current-page="currentPage"
					:page-size="pageSize"
					:total="total"
					@current-change="handlePageChange"
				></el-pagination>
			</div>
		</div>
		<div class="table-wrapper">
			<el-table
				:data="tableData"
				height="100%"
				style="width: 100%"
				size="medium"
				:header-cell-style="{
					background: '#fafafa',
					color: '#595959',
					fontWeight: 600,
					fontSize: '13px',
				}"
				:cell-style="{ fontSize: '13px', color: '#333' }"
			>
				<el-table-column prop="deviceNo" label="设备编号" min-width="120" show-overflow-tooltip />
				<el-table-column prop="address" label="安装地址" min-width="180" show-overflow-tooltip />
				<el-table-column prop="alarmType" label="告警类型" min-width="100">
					<template slot-scope="{ row }">
						<span>{{ row.alarmType }}</span>
					</template>
				</el-table-column>
				<el-table-column prop="alarmLevel" label="告警等级" min-width="90" align="center">
					<template slot-scope="{ row }">
						<el-tag :type="(levelMap[row.alarmLevel] || {}).type || 'info'" size="small" effect="light">
							{{ row.alarmLevel }}
						</el-tag>
					</template>
				</el-table-column>
				<el-table-column prop="occurTime" label="发生时间" min-width="150" />
				<el-table-column prop="duration" label="持续时间" min-width="100" align="center" />
				<el-table-column label="操作" width="160" align="center">
					<template slot-scope="{ row }">
						<el-button type="text" size="small" @click="handleViewDetail(row)">查看详情</el-button>
						<el-button
							v-if="canHandleAlarm(row)"
							type="text"
							size="small"
							style="color: #faad14"
							@click="handleAlarm(row)"
						>
							处理
						</el-button>
					</template>
				</el-table-column>
			</el-table>
		</div>
		<gc-alarm-handle-dialog
			:show.sync="showHandleDialog"
			:alarmId="alarmId"
			@ops-success="getDeviceTable"
		></gc-alarm-handle-dialog>
	</div>
</template>

<script>
import { apiGetPressureRegulatorAlarms } from '@/apis/pressureRegulatorManage.api'

export default {
	name: 'DeviceTable',
	props: {
		timeRange: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			loading: false,
			tableData: [],
			currentPage: 1,
			pageSize: 10,
			total: 0,
			alarmSeq: '0',
			showHandleDialog: false,
			alarmId: '',
			levelMap: {
				紧急: { type: 'danger' },
				重要: { type: 'warning' },
				一般: { type: 'info' },
				提示: { type: '' },
			},
		}
	},
	watch: {
		timeRange: {
			handler() {
				this.currentPage = 1
				this.getDeviceTable()
			},
			immediate: true,
		},
	},
	methods: {
		getDeviceTable() {
			this.loading = true
			apiGetPressureRegulatorAlarms({
				current: this.currentPage,
				size: this.pageSize,
				alarmSeq: this.alarmSeq,
				...this.getTimeParams(),
			})
				.then(res => {
					this.tableData = (res.records || []).map(this.normalizeRow)
					this.total = res.total || 0
				})
				.catch(() => {
					this.tableData = []
					this.total = 0
				})
				.finally(() => {
					this.loading = false
				})
		},
		normalizeRow(item) {
			return {
				...item,
				regulatorId: item.regulatorId || item.pressureRegulatorId || item.id,
				deviceNo: item.deviceNo || item.regulatorCode || '--',
				address: item.address || '--',
				alarmType: item.alarmName || '--',
				alarmLevel: this.formatLevel(item.alarmLevel),
				occurTime: item.alarmTime || '--',
				duration: this.formatDuration(item.durationMinutes),
			}
		},
		getTimeParams() {
			const [startTime] = this.timeRange || []
			return {
				alarmStartTime: startTime ? this.dayjs(startTime).format('YYYY-MM-DD HH:mm:ss') : undefined,
			}
		},
		handlePageChange(page) {
			this.currentPage = page
			this.getDeviceTable()
		},
		handleAlarmSeqChange() {
			this.currentPage = 1
			this.getDeviceTable()
		},
		handleViewDetail(row) {
			if (!row.deviceId) {
				this.$message.warning('缺少设备ID，暂无法跳转设备详情')
				return
			}
			this.$router.push({
				name: 'deviceDetail',
				query: {
					deviceId: row.deviceId,
					deviceType: row.deviceTypeId,
				},
				params: {
					name: 'AlarmRecord',
				},
			})
		},
		handleAlarm(row) {
			if (!row.alarmId) {
				this.$message.warning('缺少告警ID，暂无法处理')
				return
			}
			this.alarmId = row.alarmId
			this.showHandleDialog = true
		},
		canHandleAlarm(row) {
			return this.alarmSeq === '0' && row.alarmAckStatus !== 2 && row.manualHandleSupport === 1
		},
		formatLevel(level) {
			const map = {
				0: '一般',
				1: '重要',
				2: '紧急',
			}
			return map[level] || '--'
		},
		formatDuration(minutes) {
			if (minutes === null || minutes === undefined || minutes === '') return '--'
			const hours = Math.floor(Number(minutes) / 60)
			const mins = Number(minutes) % 60
			return hours ? `${hours}小时${mins}分` : `${mins}分`
		},
	},
}
</script>

<style lang="scss" scoped>
.device-table-section {
	background: var(--panel-bg);
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	box-shadow: var(--panel-shadow);
	backdrop-filter: blur(12px);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	height: 500px;
}
.section-header {
	height: 54px;
	padding: 0 24px;
	border-bottom: 1px solid var(--line);
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}
.section-title {
	font-size: 17px;
	font-weight: 700;
	color: var(--title);
}

.section-actions {
	display: flex;
	align-items: center;
	gap: 12px;
}

.table-wrapper {
	height: 0;
	flex: 1;
	padding: 8px 12px 12px;
}

.table-wrapper ::v-deep .el-table {
	border-radius: 12px;
	overflow: hidden;
}

.table-wrapper ::v-deep .el-table th.is-leaf,
.table-wrapper ::v-deep .el-table td {
	border-bottom-color: #edf2f7;
}

.table-wrapper ::v-deep .el-table::before {
	display: none;
}

@media (max-width: 1240px) {
	.section-header {
		flex-direction: column;
		align-items: flex-start;
	}
}
</style>
